import * as yup from 'yup';

import { normalizeUrl, yupUrlTransform, isInsecureUrl } from '@shared/utils';
import { ELiteratureType, TLiteratureContent } from '../../types';

// DOI "10.<registrant>/<suffix>" - https://www.doi.org/doi_handbook/2_Numbering.html
export const DOI_REGEX = /^10\.\d{4,9}\/\S+$/;
export const PMID_REGEX = /^\d{1,9}$/;
// Permissive middle segment so `.eN` markers can sit on either side of a range (`95.e11-7`, `1668.e1-1678.e5`).
export const PAGES_REGEX = /^(?=[A-Za-z0-9.-]*\d)[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?$/;

const currentYear = new Date().getFullYear();

export const literatureFormSchema = yup
  .object({
    type: yup.array().of(yup.string().required()).min(1, 'Type is required.').required(),
    title: yup.string().trim().required('Title is required.'),
    authors: yup.string().notRequired(),
    publicationYear: yup
      .number()
      .nullable()
      .transform((v, o) => (o === '' ? null : v))
      .min(1500, 'Year must be 1500 or later.')
      .max(currentYear + 1, `Year must not be after ${currentYear + 1}.`)
      .notRequired(),
    volume: yup.string().notRequired(),
    issue: yup.string().notRequired(),
    pages: yup
      .string()
      .notRequired()
      .test('pages-format', 'Invalid pages format. Examples: 1, 1-15, 95.e11-7.', (v) => !v || PAGES_REGEX.test(v)),
    containerTitle: yup.string().notRequired(),
    publisher: yup.string().notRequired(),
    doi: yup
      .string()
      .notRequired()
      .test('doi-format', 'DOI must look like "10.1234/example".', (v) => !v || DOI_REGEX.test(v)),
    url: yup
      .string()
      .nullable()
      .notRequired()
      .transform(yupUrlTransform)
      .test('https-only', 'Only HTTPS links are allowed.', (v) => !v || !isInsecureUrl(v))
      .url('Must be a valid URL.'),
    accessDate: yup.string().nullable().notRequired(),
    pmid: yup
      .string()
      .notRequired()
      .test('pmid-numeric', 'PMID must be numeric.', (v) => !v || PMID_REGEX.test(v)),
  })
  .required();

export type TLiteratureFormData = yup.InferType<typeof literatureFormSchema>;

export type TLiteratureContentPayload = { [K in keyof TLiteratureContent]?: TLiteratureContent[K] | null };

const splitCsv = (raw: string | null | undefined): string[] | undefined => {
  const arr = raw
    ?.split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return arr?.length ? arr : undefined;
};

// null = $unset on save (edit mode), undefined = omit on create.
export const formToContent = (data: TLiteratureFormData, isEditing: boolean): TLiteratureContentPayload => {
  const emptied = isEditing ? null : undefined;
  const pick = <T>(value: T | null | undefined | ''): T | null | undefined => (value ? value : emptied);

  const payload: TLiteratureContentPayload = {
    type: data.type[0] as ELiteratureType,
    title: data.title,
    authors: pick(splitCsv(data.authors)),
    publicationYear: pick(data.publicationYear),
    volume: pick(data.volume),
    issue: pick(data.issue),
    pages: pick(data.pages),
    containerTitle: pick(data.containerTitle),
    publisher: pick(data.publisher),
    doi: pick(data.doi),
    url: pick(data.url ? normalizeUrl(data.url) : data.url),
    accessDate: pick(data.accessDate),
    pmid: pick(data.pmid),
  };
  return payload;
};
